<template>
  <Teleport to="body"
    ><Transition name="sale-panel"
      ><div
        v-if="visible"
        class="sale-panel-layer"
        :style="zIndex ? { zIndex } : null"
        @click.self="$emit('close')"
      >
        <aside
          class="sale-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sale-panel-title"
        >
          <header>
            <div>
              <button
                v-if="backLabel"
                type="button"
                class="panel-back-link"
                @click="$emit('back')"
              >
                ← {{ backLabel }}
              </button>
              <p>Vente</p>
              <h2 id="sale-panel-title">
                {{ sale.title || "Vente sans titre" }}
              </h2>
              <span v-if="sale._key">#{{ sale._key }}</span>
            </div>
            <button type="button" aria-label="Fermer" @click="$emit('close')">
              ×
            </button>
          </header>
          <nav>
            <button
              v-for="tab in tabs"
              :key="tab.id"
              type="button"
              :class="{ active: activeTab === tab.id }"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </nav>
          <main>
            <section v-if="activeTab === 'summary'">
              <div class="hero">
                <span>{{ sourceLabel }}</span>
                <div>
                  <strong>{{ statusLabel }}</strong
                  ><small>{{
                    formatDate(sale.saleDate || sale.updated_at)
                  }}</small>
                </div>
                <b>{{ formatMoney(sale.globalPrice) }}</b>
              </div>
              <dl>
                <div>
                  <dt>Canal</dt>
                  <dd>{{ sourceLabel }}</dd>
                </div>
                <div>
                  <dt>Statut</dt>
                  <dd>{{ statusLabel }}</dd>
                </div>
                <div>
                  <dt>Objets</dt>
                  <dd>{{ itemCount }}</dd>
                </div>
                <div>
                  <dt>Prix global</dt>
                  <dd>{{ formatMoney(sale.globalPrice) }}</dd>
                </div>
              </dl>
              <article v-if="sale.description" class="note">
                {{ sale.description }}
              </article>
            </section>
            <section v-else-if="activeTab === 'items'">
              <h3>Objets de la vente</h3>
              <div v-if="items.length" class="items">
                <button
                  v-for="item in items"
                  :key="item._key || item.item_id"
                  type="button"
                  class="entity-row"
                  :title="`Voir la fiche de ${itemName(item)}`"
                  @click="$emit('open-item', item._key || item.item_id)"
                >
                  <span>{{ itemName(item).charAt(0).toUpperCase() }}</span>
                  <div>
                    <strong>{{ itemName(item) }}</strong
                    ><small>{{
                      [item.brand, item.model].filter(Boolean).join(" · ") ||
                      "Objet de collection"
                    }}</small>
                  </div>
                  <b>{{ formatMoney(item.soldPrice) }}</b>
                </button>
              </div>
              <div v-else class="empty">
                Le détail des objets n’est pas disponible dans la réponse
                actuelle.
              </div>
            </section>
            <section v-else>
              <h3>Rentabilité</h3>
              <dl>
                <div>
                  <dt>Prix de vente</dt>
                  <dd>{{ formatMoney(sale.globalPrice) }}</dd>
                </div>
                <div>
                  <dt>Frais</dt>
                  <dd>Non renseignés</dd>
                </div>
                <div>
                  <dt>Coût d’acquisition</dt>
                  <dd>Non renseigné</dd>
                </div>
                <div>
                  <dt>Marge nette</dt>
                  <dd>À calculer</dd>
                </div>
              </dl>
              <div class="empty">
                Les frais et coûts d’acquisition nécessiteront l’évolution du
                modèle de vente.
              </div>
            </section>
          </main>
          <footer>
            <button type="button" class="danger" @click="$emit('delete', sale)">
              Supprimer</button
            ><button type="button" class="secondary" @click="$emit('close')">
              Fermer</button
            ><button type="button" class="primary" @click="$emit('edit', sale)">
              Modifier la vente
            </button>
          </footer>
        </aside>
      </div></Transition
    ></Teleport
  >
</template>
<script>
import { formatDate as formatDateText } from "../../clock";
import { computed, ref, watch } from "vue";
import { saleChannelLabel } from "../../sources";
export default {
  name: "SaleQuickPanel",
  props: {
    visible: { type: Boolean, default: false },
    sale: { type: Object, default: () => ({}) },
    // Opened over another sheet (see detailStack.js)
    backLabel: { type: String, default: "" },
    zIndex: { type: Number, default: 0 },
  },
  emits: ["close", "edit", "delete", "back", "open-item"],
  setup(props) {
    const activeTab = ref("summary"),
      tabs = [
        { id: "summary", label: "Résumé" },
        { id: "items", label: "Objets" },
        { id: "profit", label: "Rentabilité" },
      ],
      sourceLabel = computed(() => saleChannelLabel(props.sale.source)),
      statusLabel = computed(
        () =>
          ({
            draft: "Brouillon",
            listed: "En ligne",
            sold: "Vendue",
            cancelled: "Annulée",
          })[props.sale.status] || "Brouillon",
      ),
      items = computed(() =>
        Array.isArray(props.sale.items) ? props.sale.items : [],
      ),
      itemCount = computed(() => props.sale.itemCount || items.value.length),
      itemName = (item) => item.name || item.title || "Objet sans nom",
      formatMoney = (value) =>
        new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "EUR",
        }).format(Number(value) || 0),
      formatDate = (value) =>
        formatDateText(value, undefined, "Date non renseignée");
    watch(
      () => props.visible,
      (visible) => {
        if (visible) activeTab.value = "summary";
      },
    );
    return {
      activeTab,
      formatDate,
      formatMoney,
      itemCount,
      itemName,
      items,
      sourceLabel,
      statusLabel,
      tabs,
    };
  },
};
</script>
<style scoped>
/* Opened over another sheet: way back to it */
.panel-back-link {
  display: inline-block;
  margin: 0 0 10px;
  padding: 0;
  border: 0;
  background: none;
  color: #2e6098;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

/* A row opens the sheet of its record */
.entity-row {
  width: 100%;
  border: 0;
  border-bottom: 1px solid #edf0f4;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.entity-row:hover,
.entity-row:focus-visible {
  background: #f5f8fb;
  outline: none;
}

.sale-panel-layer {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(15, 23, 36, 0.45);
  backdrop-filter: blur(2px);
}
.sale-panel {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  width: min(560px, 100%);
  height: 100%;
  flex-direction: column;
  background: #fff;
  box-shadow: -18px 0 55px rgba(15, 23, 36, 0.18);
  color: #202b3c;
  font-family: "DM Sans", sans-serif;
  padding-top: var(--safe-top);
  padding-bottom: var(--safe-bottom);
}
.sale-panel > header {
  display: flex;
  justify-content: space-between;
  padding: 24px 26px 20px;
  border-bottom: 1px solid #e3e8ef;
}
.sale-panel header p {
  margin: 0 0 7px;
  color: #168a64;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.sale-panel header h2 {
  max-width: 450px;
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: 20px;
  line-height: 1.35;
}
.sale-panel header span {
  display: block;
  margin-top: 6px;
  color: #84909f;
  font-size: 11px;
}
.sale-panel header > button {
  width: 35px;
  height: 35px;
  border: 1px solid #d8dfe7;
  border-radius: 8px;
  background: #fff;
  color: #657184;
  font-size: 20px;
}
.sale-panel > nav {
  display: flex;
  gap: 24px;
  padding: 0 26px;
  border-bottom: 1px solid #e3e8ef;
}
.sale-panel nav button {
  position: relative;
  height: 52px;
  border: 0;
  background: none;
  color: #7a8595;
  font-size: 12px;
  font-weight: 700;
}
.sale-panel nav button.active {
  color: #202b3c;
}
.sale-panel nav button.active:after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: #168a64;
  content: "";
}
.sale-panel > main {
  min-height: 0;
  flex: 1;
  padding: 24px 26px;
  overflow: auto;
  background: #fafbfc;
}
.hero {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 16px;
  border: 1px solid #e1e6ed;
  border-radius: 10px;
  background: #fff;
}
.hero > span {
  padding: 7px 9px;
  border-radius: 7px;
  background: #eef2f6;
  color: #506075;
  font-size: var(--meta-size);
  font-weight: 800;
}
.hero > div {
  display: flex;
  flex: 1;
  flex-direction: column;
}
.hero strong {
  font-size: 13px;
}
.hero small {
  margin-top: 4px;
  color: #8190a1;
  font-size: var(--meta-size);
}
.hero > b {
  font-size: 17px;
}
.sale-panel dl {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 16px 0;
}
.sale-panel dl div {
  padding: 13px;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  background: #fff;
}
.sale-panel dt {
  color: #84909f;
  font-size: var(--meta-size);
  text-transform: uppercase;
}
.sale-panel dd {
  margin: 5px 0 0;
  font-size: 12px;
  font-weight: 700;
}
.note {
  padding: 15px;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  background: #fff;
  color: #617084;
  font-size: 12px;
  line-height: 1.55;
}
.sale-panel h3 {
  margin: 0 0 14px;
  font-size: 15px;
}
.items {
  overflow: hidden;
  border: 1px solid #e1e6ed;
  border-radius: 9px;
  background: #fff;
}
.items article,
.items .entity-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-bottom: 1px solid #edf0f4;
}
.items article > span,
.items .entity-row > span {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 7px;
  background: #eaf7f2;
  color: #168a64;
  font-weight: 800;
}
.items article > div,
.items .entity-row > div {
  min-width: 0;
  flex: 1;
}
.items strong,
.items small {
  display: block;
}
.items strong {
  font-size: 12px;
}
.items small {
  margin-top: 3px;
  color: #84909f;
  font-size: var(--meta-size);
}
.items b {
  font-size: 11px;
}
.empty {
  padding: 22px;
  border: 1px dashed #d7dfe7;
  border-radius: 9px;
  color: #7b8797;
  font-size: 11px;
  text-align: center;
}
.sale-panel > footer {
  display: flex;
  gap: 9px;
  padding: 16px 26px;
  border-top: 1px solid #e3e8ef;
}
.sale-panel footer button {
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 800;
}
.danger {
  margin-right: auto;
  border: 0;
  background: none;
  color: #ae4741;
}
.secondary {
  border: 1px solid #d5dce5;
  background: #fff;
  color: #566276;
}
.primary {
  border: 0;
  background: #172235;
  color: #fff;
}
.sale-panel-enter-active,
.sale-panel-leave-active {
  transition: opacity 0.18s;
}
.sale-panel-enter-from,
.sale-panel-leave-to {
  opacity: 0;
}
@media (max-width: 560px) {
  .sale-panel > header,
  .sale-panel > main,
  .sale-panel > footer {
    padding-inline: 18px;
  }
  .sale-panel > nav {
    padding-inline: 18px;
  }
  .sale-panel dl {
    grid-template-columns: 1fr;
  }
}
</style>
